import { Injectable } from '@angular/core';
import _ from 'lodash-es';
import { CharacterType, ComponentType } from '../mystudies/_study_constants';

@Injectable({
  providedIn: 'root',
})
export class KeywordFilterService {
  validTextRegex = /^[* 0-9a-z]+$/;
  findAsteriskRegex = /\*/g;
  findEnterRegex = /\n/g;
  endsWithWordWithAsterisk = /[0-9a-z]+\*$/;

  processOpeningBracket(bracketsQueue: string[], prevComponentType: ComponentType): boolean {
    if (prevComponentType !== null && this.notAllowedComponentsBeforeOpeningBracket.includes(prevComponentType)) {
      return false;
    }

    bracketsQueue.push('(');
    return true;
  }

  processClosingBracket(bracketsQueue: string[], prevComponentType: ComponentType): boolean {
    if (prevComponentType !== null && this.notAllowedComponentsBeforeClosingBracket.includes(prevComponentType)) {
      return false;
    }
    if (bracketsQueue.length === 0) {
      return false; // no opening bracket
    }

    bracketsQueue.pop();
    return true;
  }

  processWord(word: string, prevComponentType: ComponentType): boolean {
    if (prevComponentType !== null && this.notAllowedComponentsBeforeWord.includes(prevComponentType)) {
      return false;
    }
    if (!this.isValidWord(word)) {
      return false;
    }

    return true;
  }

  isValidPhrase(phrase: string, inputLength: number): boolean {
    if (phrase.length === inputLength) {
      return false; // no ending quote
    }
    if (!this.validTextRegex.test(phrase)) {
      return false;
    }
    if (!this.hasValidAsterisk(phrase)) {
      return false;
    }

    return true;
  }

  isValidWord(word: string): boolean {
    if (!this.validTextRegex.test(word)) {
      return false;
    }
    if (!this.hasValidAsterisk(word)) {
      return false;
    }

    return true;
  }

  hasValidAsterisk(text: string): boolean {
    const asterisksCount = (text.match(this.findAsteriskRegex) || []).length;

    if (asterisksCount > 1) {
      return false; // multiple *
    }
    if (asterisksCount === 1 && text.length === 1) {
      return false;
    }
    if (asterisksCount === 1 && text.length !== 1 && !this.endsWithWordWithAsterisk.test(text)) {
      return false; // word does not end with *
    }

    return true;
  }

  processConnector(connector: string, prevConnector: string, prevComponentType: ComponentType): boolean {
    if (prevComponentType === null) {
      return false; // can't start with connector
    }

    if (prevComponentType !== ComponentType.Connector) {
      prevConnector = null; // to not make connection between 'not' and connector before for instance word or phrase
    }

    const isAndNotConnector = connector === this.notConnector && prevConnector !== null && prevConnector === this.andConnector;

    if (connector === this.notConnector && prevConnector !== null && prevConnector !== this.andConnector) {
      return false; // not can only be after and
    }
    if (connector === this.notConnector && prevConnector === null) {
      return false; // alone not - can only be after and
    }
    if (!isAndNotConnector && this.notAllowedComponentsBeforeConnector.includes(prevComponentType)) {
      return false;
    }

    return true;
  }

  determineCharacterType(character: string): CharacterType {
    if (character === '"') return CharacterType.Quote;
    if (character === '(') return CharacterType.OpeningBracket;
    if (character === ')') return CharacterType.ClosingBracket;
    if (character === ' ') return CharacterType.Space;

    return CharacterType.Character;
  }

  getPhrase(input: string): string {
    const quoteIndex = this.getCharIndex(input, '"');
    return input.substring(0, quoteIndex);
  }

  getWord(input: string): string {
    const spaceIndex = this.getCharIndex(input, ' ');
    const closingBracketIndex = this.getCharIndex(input, ')');
    const openingBracketIndex = this.getCharIndex(input, '(');
    const quoteIndex = this.getCharIndex(input, '"');

    const index = _.min([spaceIndex, closingBracketIndex, openingBracketIndex, quoteIndex]);
    return input.substring(0, index);
  }

  getCharIndex(input: string, char: string): number {
    const index = input.indexOf(char);
    return index === -1 ? input.length : index; // if no char in text, take till the end
  }

  notAllowedComponentsBeforeOpeningBracket: ComponentType[] = [ComponentType.Word, ComponentType.Phrase, ComponentType.ClosingBracket];
  notAllowedComponentsBeforeClosingBracket: ComponentType[] = [ComponentType.Connector, ComponentType.OpeningBracket];
  notAllowedComponentsBeforePhrase: ComponentType[] = [ComponentType.Word, ComponentType.Phrase, ComponentType.ClosingBracket];
  notAllowedComponentsBeforeConnector: ComponentType[] = [ComponentType.Connector, ComponentType.OpeningBracket];
  notAllowedComponentsBeforeWord: ComponentType[] = [ComponentType.Word, ComponentType.Phrase, ComponentType.ClosingBracket];

  notConnector = 'not';
  andConnector = 'and';
  allowedConnectors: string[] = [this.andConnector, 'or', 'near', this.notConnector];
}
